import React from "react";
import { Gem, ShieldCheck, Sparkles, BadgeCheck, Award } from "lucide-react";
import { CTAButton, Reveal, SectionTitle } from "./common";
import AutoplayYouTube from "./AutoplayYouTube";
import { useContent } from "../../context/ContentContext";

export const WhatYouGet = () => {
  const { whatYouGet } = useContent();
  const items = whatYouGet.items || [];
  return (
    <section className="py-16 bg-[#5A0B1D]">
      <div className="max-w-5xl mx-auto px-4">
        <SectionTitle light kicker={whatYouGet.kicker || "More Than A Ratnam"} title={whatYouGet.title || "A Complete Spiritual Wealth System"} />
        <div className="grid sm:grid-cols-2 gap-5">
          {items.map((item, i) => (
            <Reveal key={item.title} delay={i * 80}>
              <div className="h-full flex gap-4 bg-[#6b1024] border border-[#C9992B]/30 rounded-xl p-5 hover:border-[#C9992B]/70 transition-colors">
                <div className="shrink-0 w-11 h-11 rounded-lg bg-[#C9992B] flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-[#F4E3B8] text-lg">{item.title}</h3>
                  <p className="text-[#e0c9ac] text-sm mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-10"><CTAButton sub="Absolutely no looking back from today" /></div>
      </div>
    </section>
  );
};

export const TrustBadges = () => {
  const { trust } = useContent();
  const icons = [ShieldCheck, Sparkles, Gem, Award];
  return (
    <section className="py-14 bg-[#FBF5E9]">
      <div className="max-w-5xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(trust || []).map((b, i) => {
            const Icon = icons[i % icons.length];
            return (
              <Reveal key={b.title} delay={i * 70}>
                <div className="text-center bg-white rounded-xl border border-[#e7d9b8] p-5 h-full shadow-sm hover:shadow-md transition-shadow">
                  <div className="mx-auto w-12 h-12 rounded-full bg-[#F3E7CE] flex items-center justify-center mb-3">
                    <Icon className="w-6 h-6 text-[#B8860B]" />
                  </div>
                  <h3 className="font-display font-bold text-[#5A0B1D]">{b.title}</h3>
                  <p className="text-xs text-[#6b5647] mt-1">{b.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Showcase = () => {
  const { product, showcase } = useContent();
  const benefits = (showcase?.benefits && showcase.benefits.length ? showcase.benefits : product?.benefits) || [];
  const specs = (showcase?.showSpecs === false ? [] : product?.specifications) || [];
  return (
    <section className="py-16" style={{ background: "linear-gradient(180deg,#F3E7CE,#FBF5E9)" }}>
      <div className="max-w-2xl mx-auto px-4">
        <Reveal>
          <span className="font-marcellus tracking-[0.25em] text-xs uppercase text-[#B8860B]">{showcase?.kicker || "Certified & Energised"}</span>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#5A0B1D] mt-2">{showcase?.title || "Not Just a Ratnam — A Result-Driven Wealth Companion"}</h2>
          <ul className="mt-5 space-y-3">
            {benefits.map((t) => (
              <li key={t} className="flex items-start gap-3 text-[#4a3a2e]">
                <BadgeCheck className="w-5 h-5 text-[#C9992B] shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>

          {specs.length > 0 && (
            <div className="mt-6 rounded-xl border border-[#e7d9b8] bg-white p-5">
              <h4 className="font-display font-bold text-[#5A0B1D] mb-3">Specifications</h4>
              {specs.map((p) => (
                <div key={p.key} className="flex justify-between text-sm py-1 text-[#4a3a2e]">
                  <span>{p.key}</span><span className="font-medium">{p.value}</span>
                </div>
              ))}
              <div className="section-divider my-3" />
              <div className="flex justify-between font-bold text-[#5A0B1D]">
                <span>Price</span><span>₹{Number(product?.final_price || product?.price || 0).toLocaleString("en-IN")}</span>
              </div>
            </div>
          )}
          <div className="mt-7"><CTAButton /></div>
        </Reveal>
      </div>
    </section>
  );
};

export const SuccessStories = () => {
  const { success } = useContent();
  const shorts = (success.shorts || []).slice(0, 5);
  return (
    <section className="py-16 bg-[#3a0713]">
      <div className="max-w-6xl mx-auto px-4">
        <SectionTitle light kicker={success.kicker || "Real People, Real Blessings"} title={success.title || "Success Stories"} />
        <div className="flex gap-5 overflow-x-auto pb-4 snap-x md:grid md:grid-cols-5 md:overflow-visible">
          {shorts.map((s, i) => (
            <Reveal key={s.id + i} delay={i * 90} className="snap-start shrink-0 w-[70%] sm:w-[45%] md:w-auto">
              <div className="rounded-xl overflow-hidden border-2 border-[#C9992B]/40 bg-black shadow-lg">
                <div className="relative" style={{ aspectRatio: "9 / 16" }}>
                  <AutoplayYouTube id={s.id} className="absolute inset-0 w-full h-full" title={s.name} />
                </div>
                <div className="bg-[#5A0B1D] px-3 py-2 text-center">
                  <div className="text-[#F4E3B8] text-sm font-semibold">{s.name}</div>
                  <div className="text-[#c9b59a] text-xs">{s.place}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="text-center text-[#c9b59a] text-xs mt-3 md:hidden">Swipe to see more →</p>
        <div className="text-center mt-10"><CTAButton sub="There is absolutely no looking back from today" /></div>
      </div>
    </section>
  );
};
